import { enrichSimulationRowAdvanceFromPlaylist } from '@/helpers/show-info-advance.helper';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto';
import type { FlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import { getFlowPlaylist, getSessionPlaylist, type SessionPlaylistSource } from '@/helpers/flow-media-playlist.helper';
import { mediaHasPlayablePresentationSlides } from '@/helpers/media-processing-insight.helper';
import { resolveSessionForFlowSlot } from '@/helpers/simulation-flow.helper';
import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';

const FLOW_PLAYLIST_SCOPES = new Set<FlowPlaylistScope>([
  'pre_flow',
  'flow_intro',
  'flow_transition',
  'flow_resume',
  'flow_outro',
  'after_flow',
]);

function mediaHasApprovedAsset(media: MediaDto | undefined): boolean {
  return media?.assets?.some((asset) => asset.isApproved) ?? false;
}

function shouldExcludeMediaCueRowWithoutApprovedAsset(
  row: SimulationCueSheetRow,
  mediaById: Map<string, MediaDto>,
): boolean {
  if (!row.itemId.startsWith('media:')) {
    return false;
  }
  return !mediaHasApprovedAsset(mediaById.get(row.itemId.slice('media:'.length)));
}

/** Drop media cue rows with no approved asset (aligns with flow-control media list). */
export function filterSimulationCueRowsForApprovedMedia(
  rows: SimulationCueSheetRow[],
  mediaById: Map<string, MediaDto>,
): SimulationCueSheetRow[] {
  const filtered = rows.filter((row) => !shouldExcludeMediaCueRowWithoutApprovedAsset(row, mediaById));
  return filtered.map((row, index) => ({ ...row, ordinal: index }));
}

/** Align simulation cue rows with flow-control media list playability (approved assets, enabled virtual). */
export function applySimulationCuePlayability(rows: SimulationCueSheetRow[], flow: FlowDto): SimulationCueSheetRow[] {
  const mediaById = collectFlowMediaById(flow);
  const withPlayability = rows.map((row) => {
    const withAdvance = enrichSimulationRowAdvanceFromPlaylist(flow, row);
    return {
      ...withAdvance,
      isPlayable: computeRowPlayable(withAdvance, mediaById, flow),
    };
  });
  return filterSimulationCueRowsForApprovedMedia(withPlayability, mediaById);
}

export function collectFlowMediaById(flow: FlowDto): Map<string, MediaDto> {
  const map = new Map<string, MediaDto>();
  const add = (items?: MediaDto[]) => {
    for (const item of items ?? []) {
      map.set(item.id, item);
    }
  };
  add(flow.media);
  for (const slot of flow.slots ?? []) {
    if (slot.content && typeof slot.content === 'object' && 'media' in slot.content) {
      add(slot.content.media as MediaDto[]);
    }
    if (slot.contentType === 'Session' && slot.id) {
      add(resolveSessionForFlowSlot(flow, slot.id)?.media);
    }
  }
  return map;
}

function resolveSessionPlaylistForFlowSlot(flow: FlowDto, flowSlotId: string): MediaPlaylistDto | undefined {
  // Event plan session (store + query cache) is authoritative — same as flow-control media list.
  const planSession = resolveSessionForFlowSlot(flow, flowSlotId);
  const planPlaylist = planSession ? getSessionPlaylist(planSession) : undefined;
  if (planPlaylist) {
    return planPlaylist;
  }

  const slot = flow.slots?.find((entry) => entry.id === flowSlotId);
  const embedded = slot?.content;
  if (embedded && typeof embedded === 'object' && 'mediaPlaylists' in embedded) {
    return getSessionPlaylist(embedded as SessionPlaylistSource);
  }
  return undefined;
}

/** Flow slot id for a cue row — falls back to segmentAnchor `slot:<id>`. */
export function resolveFlowSlotIdFromCueRow(row: SimulationCueSheetRow): string | undefined {
  return row.slotId ?? (row.segmentAnchor?.startsWith('slot:') ? row.segmentAnchor.slice('slot:'.length) : undefined);
}

/** Resolve the playlist that owns this cue row (virtual toggles are per playlist). */
export function resolvePlaylistForCueRow(flow: FlowDto, row: SimulationCueSheetRow): MediaPlaylistDto | undefined {
  const flowSlotId = resolveFlowSlotIdFromCueRow(row);
  const scope =
    row.scope ?? (row.segmentKind === 'session' || row.segmentKind === 'break' ? row.segmentKind : undefined);

  if (scope === 'session' && flowSlotId) {
    return resolveSessionPlaylistForFlowSlot(flow, flowSlotId);
  }

  if (scope === 'slot' && flowSlotId) {
    return getFlowPlaylist(flow, 'slot', flowSlotId);
  }

  if (scope && FLOW_PLAYLIST_SCOPES.has(scope as FlowPlaylistScope)) {
    return getFlowPlaylist(flow, scope as FlowPlaylistScope);
  }

  // Legacy rows without scope: infer from segmentKind.
  if (row.segmentKind === 'session' && flowSlotId) {
    return resolveSessionPlaylistForFlowSlot(flow, flowSlotId);
  }

  if (FLOW_PLAYLIST_SCOPES.has(row.segmentKind as FlowPlaylistScope)) {
    return getFlowPlaylist(flow, row.segmentKind as FlowPlaylistScope);
  }

  if (flowSlotId) {
    return getFlowPlaylist(flow, 'slot', flowSlotId);
  }

  return undefined;
}

function getPlaylistEnabledVirtualSlugs(playlist: MediaPlaylistDto): Set<string> {
  // Match playout and operator read-only UI: effectiveVirtualMedia is the source of truth.
  return new Set(playlist.effectiveVirtualMedia?.enabledSlugs ?? []);
}

function resolveVirtualSlugFromRow(row: SimulationCueSheetRow): string | undefined {
  if (row.presentationKind !== 'composite' && !row.itemId.startsWith('composite:')) {
    return undefined;
  }
  return row.label;
}

function isVirtualSlugPlayableInPlaylist(virtualSlug: string, playlist: MediaPlaylistDto | undefined): boolean {
  if (!playlist) {
    return false;
  }
  const applicable = playlist.applicableVirtualMedia ?? [];
  if (!applicable.some((item) => item.slug === virtualSlug)) {
    return false;
  }
  return getPlaylistEnabledVirtualSlugs(playlist).has(virtualSlug);
}

function isAgentPresentationPlayableInSimulation(
  row: SimulationCueSheetRow,
  mediaById: Map<string, MediaDto>,
): boolean {
  if (!row.itemId.startsWith('media:')) {
    return Boolean(row.fallbackCompositeItemId);
  }
  const media = mediaById.get(row.itemId.slice('media:'.length));
  if (mediaHasPlayablePresentationSlides(media)) {
    return mediaHasApprovedAsset(media);
  }
  if (row.isAutoOnly) {
    return Boolean(row.fallbackCompositeItemId);
  }
  return false;
}

function computeRowPlayable(row: SimulationCueSheetRow, mediaById: Map<string, MediaDto>, flow: FlowDto): boolean {
  if (row.isAutoOnly) {
    if (row.presentationKind === 'agent') {
      return isAgentPresentationPlayableInSimulation(row, mediaById);
    }
    const virtualSlug = resolveVirtualSlugFromRow(row);
    if (virtualSlug) {
      return isVirtualSlugPlayableInPlaylist(virtualSlug, resolvePlaylistForCueRow(flow, row));
    }
    return (
      row.presentationKind === 'composite' ||
      row.itemId.startsWith('composite:') ||
      row.itemId.startsWith('virtualMedia:')
    );
  }
  if (row.presentationKind === 'agent') {
    return isAgentPresentationPlayableInSimulation(row, mediaById);
  }

  if (row.itemId.startsWith('media:')) {
    const mediaId = row.itemId.slice('media:'.length);
    return mediaHasApprovedAsset(mediaById.get(mediaId));
  }

  const virtualSlug = resolveVirtualSlugFromRow(row);
  if (virtualSlug) {
    return isVirtualSlugPlayableInPlaylist(virtualSlug, resolvePlaylistForCueRow(flow, row));
  }

  return true;
}
